// FlavorDetailModal.tsx
import React, { useState } from 'react';
import { X, Star, Save, Image as ImageIcon, Check, PackagePlus } from 'lucide-react';
import { FlavorDetails, FlavorRepository } from '../repositories/LocalDatabase';
import RadarChart from './RadarChart';
import FlavorCardPopup from './FlavorCardPopup';
import { showHUD } from './NotificationHUD';

interface FlavorDetailModalProps {
  flavor: FlavorDetails;
  onClose: () => void;
  onSaveSuccess: () => void;
}

// 4軸の定義（チャート描画 & テーブル表示で共用）
const AXES_KEYS = [
  { key: 'sweetness', label: '甘さ' },
  { key: 'sourness', label: '酸味' },
  { key: 'coolness', label: '清涼感' },
  { key: 'fruitiness', label: '果実感' },
] as const;

type AxisKey = (typeof AXES_KEYS)[number]['key'];

// ===== スコアバー描画（テーブル用） =====
function ScoreBar({
  value,
  color,
}: {
  value: number;
  color: string;
}) {
  const pct = Math.round(((value - 1) / 4) * 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <div
        style={{
          flex: 1,
          height: '6px',
          borderRadius: '3px',
          backgroundColor: 'rgba(231, 228, 223, 0.08)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            borderRadius: '3px',
            backgroundColor: color,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
      <span style={{ fontSize: '12px', fontWeight: 'bold', color, minWidth: '24px', textAlign: 'right' }}>
        {value.toFixed(1)}
      </span>
    </div>
  );
}

// ===== 7軸比較テーブル =====
function TasteComparisonTable({
  masterVector,
  userVector,
}: {
  masterVector?: FlavorDetails['master_taste_vector'];
  userVector?: FlavorDetails['user_taste_vector'];
}) {
  if (!masterVector && !userVector) return null;

  return (
    <div
      style={{
        marginTop: '12px',
        borderRadius: '10px',
        overflow: 'hidden',
        border: '1px solid rgba(231, 228, 223, 0.1)',
        fontSize: '13px',
      }}
    >
      {/* テーブルヘッダー */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '70px 1fr 1fr',
          backgroundColor: 'rgba(26, 28, 31, 0.7)',
          borderBottom: '1px solid rgba(231, 228, 223, 0.1)',
        }}
      >
        <div style={{ padding: '8px 10px', opacity: 0.5 }}>軸</div>
        <div
          style={{
            padding: '8px 10px',
            color: 'rgba(201, 166, 107, 0.85)',
            fontWeight: 'bold',
            borderLeft: '1px solid rgba(231, 228, 223, 0.08)',
          }}
        >
          マスター
        </div>
        <div
          style={{
            padding: '8px 10px',
            color: 'rgba(107, 78, 255, 0.9)',
            fontWeight: 'bold',
            borderLeft: '1px solid rgba(231, 228, 223, 0.08)',
          }}
        >
          マイ評価
        </div>
      </div>

      {/* 軸ごとの行 */}
      {AXES_KEYS.map(({ key, label }, idx) => (
        <div
          key={key}
          style={{
            display: 'grid',
            gridTemplateColumns: '70px 1fr 1fr',
            backgroundColor:
              idx % 2 === 0
                ? 'rgba(26, 28, 31, 0.4)'
                : 'rgba(26, 28, 31, 0.2)',
            borderBottom:
              idx < AXES_KEYS.length - 1
                ? '1px solid rgba(231, 228, 223, 0.06)'
                : 'none',
          }}
        >
          <div style={{ padding: '9px 10px', opacity: 0.7 }}>{label}</div>

          {/* マスター列 */}
          <div
            style={{
              padding: '9px 10px',
              borderLeft: '1px solid rgba(231, 228, 223, 0.08)',
            }}
          >
            {masterVector ? (
              <ScoreBar value={masterVector[key]} color="rgba(201, 166, 107, 0.8)" />
            ) : (
              <span style={{ opacity: 0.35, fontSize: '12px' }}>—</span>
            )}
          </div>

          {/* マイ評価列 */}
          <div
            style={{
              padding: '9px 10px',
              borderLeft: '1px solid rgba(231, 228, 223, 0.08)',
            }}
          >
            {userVector ? (
              <ScoreBar value={userVector[key]} color="rgba(107, 78, 255, 0.9)" />
            ) : (
              <span style={{ opacity: 0.35, fontSize: '12px' }}>—</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ===== メインコンポーネント =====
export default function FlavorDetailModal({
  flavor,
  onClose,
  onSaveSuccess,
}: FlavorDetailModalProps) {
  const [isClosing, setIsClosing] = useState(false);
  const [owned, setOwned] = useState(flavor.owned);
  const [rating, setRating] = useState(flavor.rating || 5);

  const handleClose = () => {
    const animationsEnabled = localStorage.getItem('animationsEnabled') !== 'false';
    if (!animationsEnabled) {
      onClose();
      return;
    }
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 500);
  };
  const [memo, setMemo] = useState(flavor.memo || '');
  const [nicotine, setNicotine] = useState(flavor.nicotine !== false);
  const [tasteVector, setTasteVector] = useState<FlavorDetails['taste_vector']>({
    sweetness: flavor.taste_vector.sweetness,
    sourness: flavor.taste_vector.sourness,
    coolness: flavor.taste_vector.coolness,
    fruitiness: flavor.taste_vector.fruitiness,
  });

  const [showShareModal, setShowShareModal] = useState(false);

  const handleSliderChange = (key: AxisKey, value: number) => {
    setTasteVector((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    await FlavorRepository.saveUserFlavor(
      flavor.id,
      owned,
      rating,
      memo,
      tasteVector,
      nicotine,
    );
    showHUD({ type: 'success', message: 'フレーバー設定を保存しました' });
    onSaveSuccess();
    handleClose();
  };

  const handleToggleOwned = async () => {
    const newOwned = !owned;
    setOwned(newOwned);
    await FlavorRepository.saveUserFlavor(
      flavor.id,
      newOwned,
      rating,
      memo,
      tasteVector,
      nicotine,
    );
    showHUD({ type: 'success', message: newOwned ? '所持済みにしました' : '未所持にしました' });
    onSaveSuccess();
  };

  const handleKeyDownBackdrop = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  // チャートに渡すベクトルを計算
  // - userValues: スライダー操作中はリアルタイム更新（保存前でも反映）
  //   ただし owned=false の場合は DB の user_taste_vector (未編集) をそのまま使う
  const chartUserValues = owned
    ? tasteVector
    : flavor.user_taste_vector ?? undefined;

  // マスターデータが存在するかどうか
  const hasMaster = flavor.master_taste_vector !== undefined;

  // 凡例を表示するか（マスターがあり、かつ個人評価もある場合）
  const showLegend = hasMaster && chartUserValues !== undefined;

  return (
    <>
      <div
        className={`modal-backdrop ${isClosing ? 'closing' : ''}`}
        onClick={handleClose}
        onKeyDown={handleKeyDownBackdrop}
        role="button"
        tabIndex={0}
        aria-label="モーダルを閉じる"
        style={{ zIndex: 2000 }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            zIndex: -1,
            pointerEvents: 'none',
          }}
        />
        <div
          className="modal-container"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          style={{
            maxWidth: '800px',
            width: '92%',
            padding: '24px',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '20px',
            }}
          >
            <div>
              <span
                style={{
                  fontSize: 'var(--font-size-caption)',
                  color: 'var(--color-accent)',
                  fontFamily: 'var(--font-heading-en)',
                }}
              >
                {flavor.brand_name}
              </span>
              <h2
                style={{
                  fontSize: 'var(--font-size-h2)',
                  fontFamily: 'var(--font-heading-ja)',
                  margin: '4px 0 0 0',
                }}
              >
                {flavor.name}
              </h2>
              {flavor.series_name && (
                <span style={{ fontSize: '13px', opacity: 0.6 }}>
                  系統: {flavor.series_name}
                </span>
              )}
            </div>
            <button
              onClick={handleClose}
              style={{
                background: 'rgba(231, 228, 223, 0.1)',
                borderRadius: '50%',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-text)',
              }}
            >
              <X size={18} />
            </button>
          </div>

          <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
            {/* Left Column - Form Inputs */}
            <div
              style={{
                flex: '1 1 350px',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
              }}
            >
              {flavor.description && (
                <div
                  style={{
                    backgroundColor: 'rgba(26, 28, 31, 0.5)',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(231, 228, 223, 0.05)',
                  }}
                >
                  <h4
                    style={{
                      fontSize: '13px',
                      color: 'var(--color-accent)',
                      margin: '0 0 6px 0',
                      fontWeight: 'bold',
                    }}
                  >
                    公式解説
                  </h4>
                  <p
                    style={{
                      margin: 0,
                      fontSize: '14px',
                      lineHeight: '1.5',
                      opacity: 0.8,
                    }}
                  >
                    {flavor.description}
                  </p>
                </div>
              )}

              {/* Owned Toggle Button */}
              <button
                type="button"
                onClick={handleToggleOwned}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  borderRadius: '24px',
                  backgroundColor: owned
                    ? 'rgba(91, 161, 139, 0.15)'
                    : 'rgba(231, 228, 223, 0.08)',
                  border: owned
                    ? '1px solid rgba(91, 161, 139, 0.4)'
                    : '1px solid rgba(231, 228, 223, 0.2)',
                  color: owned ? 'var(--color-success)' : 'rgba(231, 228, 223, 0.7)',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-out',
                  width: 'fit-content',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = owned
                    ? 'rgba(91, 161, 139, 0.25)'
                    : 'rgba(231, 228, 223, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = owned
                    ? 'rgba(91, 161, 139, 0.15)'
                    : 'rgba(231, 228, 223, 0.08)';
                }}
              >
                {owned ? <Check size={18} /> : <PackagePlus size={18} />}
                {owned ? '所持済み' : '未所持'}
              </button>

              {/* Nicotine Toggle */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                <span
                  style={{
                    fontSize: '14px',
                    color: 'var(--color-accent)',
                    fontWeight: 'bold',
                  }}
                >
                  ニコチン
                </span>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setNicotine(true)}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '8px',
                      backgroundColor: nicotine
                        ? 'var(--color-primary)'
                        : 'rgba(231, 228, 223, 0.05)',
                      border: '1px solid rgba(231, 228, 223, 0.1)',
                      color: 'var(--color-text)',
                      fontWeight: nicotine ? 'bold' : 'normal',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    あり
                  </button>
                  <button
                    type="button"
                    onClick={() => setNicotine(false)}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '8px',
                      backgroundColor: !nicotine
                        ? 'var(--color-success)'
                        : 'rgba(231, 228, 223, 0.05)',
                      border: '1px solid rgba(231, 228, 223, 0.1)',
                      color: 'var(--color-text)',
                      fontWeight: !nicotine ? 'bold' : 'normal',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    ノンニコチン
                  </button>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  animation: 'fadeIn 0.3s ease-out',
                }}
              >
                  {/* Rating */}
                  <div>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        marginBottom: '8px',
                        color: 'var(--color-accent)',
                      }}
                    >
                      マイ評価
                    </span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {Array.from({ length: 5 }).map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setRating(idx + 1)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                          }}
                        >
                          <Star
                            size={24}
                            fill={
                              idx < rating
                                ? 'var(--color-accent)'
                                : 'transparent'
                            }
                            color={
                              idx < rating
                                ? 'var(--color-accent)'
                                : 'rgba(231, 228, 223, 0.2)'
                            }
                            style={{ cursor: 'pointer' }}
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Memo */}
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        marginBottom: '8px',
                        color: 'var(--color-accent)',
                      }}
                    >
                      テイスティングメモ
                    </label>
                    <textarea
                      placeholder="吸った感想や、相性の良いフレーバーなどのメモ..."
                      value={memo}
                      onChange={(e) => setMemo(e.target.value)}
                      style={{
                        width: '100%',
                        height: '90px',
                        backgroundColor: 'rgba(26, 28, 31, 0.6)',
                        border: '1px solid rgba(231, 228, 223, 0.1)',
                        borderRadius: '8px',
                        color: 'var(--color-text)',
                        padding: '10px',
                        fontSize: '14px',
                        resize: 'none',
                        outline: 'none',
                      }}
                      onFocus={(e) =>
                        (e.target.style.borderColor = 'var(--color-primary)')
                      }
                      onBlur={(e) =>
                        (e.target.style.borderColor =
                          'rgba(231, 228, 223, 0.1)')
                      }
                    />
                  </div>

                  {/* Taste Sliders */}
                  <div>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        marginBottom: '12px',
                        color: 'var(--color-accent)',
                      }}
                    >
                      マイ味覚評価 (1〜5段階)
                    </span>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                      }}
                    >
                      {AXES_KEYS.map(({ key, label }) => (
                        <div
                          key={key}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '12px',
                          }}
                        >
                          <span style={{ fontSize: '13px', width: '60px' }}>
                            {label}
                          </span>
                          <input
                            type="range"
                            min="1"
                            max="5"
                            step="0.5"
                            value={tasteVector[key]}
                            onChange={(e) =>
                              handleSliderChange(
                                key,
                                parseFloat(e.target.value),
                              )
                            }
                            style={{
                              flex: 1,
                              accentColor: 'var(--color-primary)',
                            }}
                          />
                          <span
                            style={{
                              fontSize: '13px',
                              width: '25px',
                              textAlign: 'right',
                              fontWeight: 'bold',
                            }}
                          >
                            {tasteVector[key].toFixed(1)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
            </div>

            {/* Right Column - Chart */}
            <div
              style={{
                flex: '1 1 260px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              {/* チャートヘッダー */}
              <div style={{ width: '100%', textAlign: 'center' }}>
                <h4
                  style={{
                    fontSize: '14px',
                    color: 'var(--color-accent)',
                    margin: '0 0 4px 0',
                  }}
                >
                  味覚バランス
                </h4>
                {/* マスターなし（自作フレーバー等）の場合は注記を表示 */}
                {!hasMaster && (
                  <span
                    style={{
                      fontSize: '11px',
                      opacity: 0.45,
                      fontStyle: 'italic',
                    }}
                  >
                    ※ マスターデータ未登録
                  </span>
                )}
              </div>

              {/* レーダーチャート本体 */}
              <RadarChart
                sweetness={tasteVector.sweetness}
                sourness={tasteVector.sourness}
                coolness={tasteVector.coolness}
                fruitiness={tasteVector.fruitiness}
                size={220}
                masterValues={flavor.master_taste_vector}
                userValues={tasteVector}
                showLegend={showLegend}
              />

              {/* 数値比較テーブル */}
              <div style={{ width: '100%' }}>
                <TasteComparisonTable
                  masterVector={flavor.master_taste_vector}
                  userVector={owned ? tasteVector : flavor.user_taste_vector}
                />
              </div>

              {/* フレーバーカード作成ボタン */}
              <button
                onClick={() => setShowShareModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(201, 166, 107, 0.1)',
                  border: '1px solid rgba(201, 166, 107, 0.3)',
                  color: 'var(--color-accent)',
                  fontSize: '13px',
                  width: '100%',
                  justifyContent: 'center',
                  marginTop: '6px',
                  cursor: 'pointer',
                }}
              >
                <ImageIcon size={16} />
                フレーバーカードを作成
              </button>
            </div>
          </div>

          {/* Footer Save Actions */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '30px',
              borderTop: '1px solid rgba(231, 228, 223, 0.1)',
              paddingTop: '16px',
            }}
          >
            <button
              onClick={handleClose}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                backgroundColor: 'transparent',
                border: '1px solid rgba(231, 228, 223, 0.1)',
                color: 'var(--color-text)',
                fontSize: '14px',
              }}
            >
              キャンセル
            </button>
            <button
              onClick={handleSave}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 24px',
                borderRadius: '8px',
                backgroundColor: 'var(--color-primary)',
                color: 'var(--color-text)',
                fontSize: '14px',
                boxShadow: '0 4px 15px rgba(107, 78, 255, 0.3)',
              }}
            >
              <Save size={16} />
              設定を保存する
            </button>
          </div>
        </div>
      </div>

      {showShareModal && (
        <FlavorCardPopup
          flavor={flavor}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </>
  );
}
